import { formatTies, formatWeekList } from '../../utils/predictionScorecard';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function shortDate(isoDate) {
  const [, month, day] = String(isoDate).split('-').map(Number);
  return month && day ? `${MONTHS[month - 1]} ${day}` : isoDate;
}

function weekSpan(weeks) {
  if (!weeks.length) return '';
  if (weeks.length === 1) return `Week ${weeks[0]}`;
  const contiguous = weeks.every((week, index) => index === 0 || week === weeks[index - 1] + 1);
  return contiguous ? `Weeks ${weeks[0]}–${weeks[weeks.length - 1]}` : formatWeekList(weeks);
}

const brier = (value) => (value == null ? '—' : value.toFixed(3));
const wins = (value) => value.toFixed(1);

function CohortStats({ cohort, label }) {
  const { ai, coinFlip, baseline } = cohort;
  const showSameGames = baseline.n > 0 && baseline.n !== ai.n;
  return <>
    <p className="forecast-prob__wins">
      <span><b>{ai.actualWins}</b> wins</span>
      <span><b>{wins(ai.expectedWins)}</b> expected</span>
      <small>90% range: {wins(Math.max(0, ai.bandLow))} to {wins(Math.min(ai.n, ai.bandHigh))} wins over {ai.n} game{ai.n === 1 ? '' : 's'}</small>
    </p>
    <table className="forecast-prob__table">
      <caption className="forecast-sr">Win-chance grades, {label}</caption>
      <thead><tr><th scope="col"><span className="forecast-sr">Method</span></th><th scope="col">Picks right</th><th scope="col">Brier <small>lower is better</small></th></tr></thead>
      <tbody>
        <tr className="is-ai"><th scope="row">AI picks</th><td>{ai.actualWins}/{ai.n}</td><td>{brier(ai.brier)}</td></tr>
        <tr><th scope="row">Coin flip</th><td>{wins(coinFlip.expectedWins)}/{ai.n} <small>on average</small></td><td>{brier(coinFlip.brier)}</td></tr>
        <tr><th scope="row">Season average</th><td>{baseline.n ? `${baseline.correct}/${baseline.n}` : '—'}</td><td>{brier(baseline.brier)}</td></tr>
        {showSameGames && <tr className="is-same-games"><th scope="row">AI, same games</th><td>{baseline.ai.correct}/{baseline.n}</td><td>{brier(baseline.ai.brier)}</td></tr>}
      </tbody>
    </table>
  </>;
}

function CohortNotes({ cohort }) {
  const notes = [];
  if (cohort.ties) notes.push(`${formatTies(cohort.ties)}.`);
  if (cohort.baseline.noBaselineWeeks.length) {
    const { noBaselineWeeks, weeks, n } = cohort.baseline;
    const covered = weeks.length ? ` The season-average row covers ${weekSpan(weeks)} (${n} game${n === 1 ? '' : 's'}).` : '';
    notes.push(`${weekSpan(noBaselineWeeks)} had no earlier final scores, so the season average has no pick there.${covered}`);
  }
  if (cohort.baseline.noPick) notes.push(`${cohort.baseline.noPick} game${cohort.baseline.noPick === 1 ? '' : 's'} had equal season averages, so the season average made no pick.`);
  const pending = cohort.pendingWeeks;
  if (pending.length && cohort.ai.n) notes.push(`${formatWeekList(pending)} ${pending.length === 1 ? 'is' : 'are'} waiting for final scores.`);
  if (cohort.lateExcludedWeeks?.length) notes.push(`${formatWeekList(cohort.lateExcludedWeeks)} ${cohort.lateExcludedWeeks.length === 1 ? 'was' : 'were'} captured after first kickoff, so ${cohort.lateExcludedWeeks.length === 1 ? 'it is' : 'they are'} left out.`);
  if (!notes.length) return null;
  return <ul className="forecast-prob__notes">{notes.map((note) => <li key={note}>{note}</li>)}</ul>;
}

function Prospective({ scorecard }) {
  const cohort = scorecard.prospective;
  const label = `Before kickoff (Week ${scorecard.firstProspectiveWeek} on)`;
  const firstWeek = cohort.weeks[0] ?? scorecard.firstProspectiveWeek;
  return <article className="forecast-prob__cohort is-prospective" aria-labelledby="forecast-prob-prospective">
    <p className="forecast-kicker">Issued before the games</p>
    <h4 id="forecast-prob-prospective">{label}</h4>
    {cohort.ai.n
      ? <CohortStats cohort={cohort} label={label} />
      : <p className="forecast-prob__waiting"><b>Waiting for Week {firstWeek} finals.</b> {cohort.weeks.length ? `The Week ${firstWeek} win chances were set before kickoff and get graded here once final scores are in.` : `Win chances start with Week ${firstWeek}.`}</p>}
    <CohortNotes cohort={cohort} />
  </article>;
}

function AfterTheFact({ scorecard }) {
  const cohort = scorecard.afterTheFact;
  if (!cohort.weeks.length) return null;
  const label = `Calculated after the fact (${weekSpan(cohort.weeks)})`;
  return <article className="forecast-prob__cohort is-after" aria-labelledby="forecast-prob-after">
    <p className="forecast-kicker">Not issued before the games</p>
    <h4 id="forecast-prob-after">{label}</h4>
    <p className="forecast-prob__retro">These use the win-probability formula registered on {shortDate(scorecard.registeredAt)}, applied to the original score forecasts; they were not issued before the games.</p>
    {cohort.ai.n ? <CohortStats cohort={cohort} label={label} /> : <p className="forecast-prob__waiting">No final scores yet.</p>}
    <CohortNotes cohort={cohort} />
  </article>;
}

// probability: { status: 'loading' | 'error' | 'ready', scorecard } from buildProbabilityScorecard.
export default function ProbabilityPanel({ probability }) {
  if (!probability) return null;
  const { status, scorecard } = probability;
  return <section className="forecast-prob" aria-labelledby="forecast-prob-title">
    <div className="forecast-prob__intro">
      <p className="forecast-kicker">Win chances, graded</p>
      <h3 id="forecast-prob-title">How good are these probabilities?</h3>
      <p>Each pick gets a win chance from the gap between the two score forecasts. If the chances are honest, the picks win about as many games as the chances add up to. The Brier score checks each chance against what happened. Lower is better: 0 is perfect, and calling every game 50–50 scores 0.250.</p>
      <p className="forecast-prob__caveat">One season of picks can't prove the AI is skillful; score error is the better yardstick.</p>
    </div>
    {status === 'ready' && scorecard && !scorecard.missingWeeks.length
      ? <div className="forecast-prob__cohorts"><Prospective scorecard={scorecard} /><AfterTheFact scorecard={scorecard} /></div>
      : <p className="forecast-prob__status" role="status">{status === 'loading' ? 'Loading the win chances…' : 'The win chances could not be loaded right now.'}</p>}
  </section>;
}
